import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articleTypesApi, type ArticleTypeView } from "../api/articles";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Pagination } from "../components/Pagination";
import { TypeCatalogSwitch } from "../components/TypeCatalogSwitch";
import Ic from "../components/icons.jsx";

const PAGE_SIZE = 25;
const DESCRIPTION_MAX = 90;

/** `/tipovi/artikli` — the article-type catalogue (sprint 5l §7), the second view of "Tipovi delova" (2026-10-12). */
export default function ArticleTypesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("article-types:create");

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(0);

  const [items, setItems] = useState<ArticleTypeView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  // A search change restarts at the first page — without an extra fetch of the stale page in between.
  const prevQ = useRef(debouncedQ);

  useEffect(() => {
    if (prevQ.current !== debouncedQ) {
      prevQ.current = debouncedQ;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }
    let cancelled = false;
    setError(null);
    articleTypesApi
      .list({ q: debouncedQ || undefined, page, size: PAGE_SIZE, sort: "name,asc" })
      .then((res) => {
        if (cancelled) return;
        setItems(res.content);
        setTotal(res.totalElements);
        setTotalPages(res.totalPages);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setError(err.message);
        setItems([]);
      });
    return () => { cancelled = true; };
  }, [debouncedQ, page]);

  const hasFilters = debouncedQ !== "";
  const colCode = t("articleTypes.col.code", "Šifra");
  const colName = t("articleTypes.col.name", "Naziv");
  const colAttributes = t("articleTypes.col.attributeCount", "Atributa");
  const colArticles = t("articleTypes.col.articleCount", "Artikala");
  const colDescription = t("articleTypes.col.description", "Opis");
  const dash = <span className="muted">—</span>;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("articleTypes.title", "Tipovi artikala")}</h1>
          <div className="sub">
            {items === null
              ? t("common.loading", "Učitavanje…")
              : t("articleTypes.count", "{{count}} tipova artikala", { count: total })}
          </div>
        </div>
        <div className="page-actions">
          <TypeCatalogSwitch value="articles" />
          {canCreate && (
            <button type="button" className="btn primary" onClick={() => navigate("/tipovi/artikli/novi")}>
              <Ic.plus /> {t("articleTypes.new", "Nov tip artikla")}
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("articleTypes.filter.searchPlaceholder", "Pretraga po šifri ili nazivu…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {hasFilters && (
          <button type="button" className="btn sm ghost" onClick={() => setQ("")}>
            {t("action.clear", "Očisti")}
          </button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <div className="card">
        {items === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : items.length === 0 ? (
          <div className="empty">
            {hasFilters
              ? t("articleTypes.emptyFiltered", "Nijedan tip artikla ne odgovara pretrazi.")
              : t("articleTypes.empty", "Još nema tipova artikala. Dodajte prvi tip artikla (npr. Ležaj, Šraf, Filter vreća).")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{colCode}</th>
                  <th>{colName}</th>
                  <th className="num">{colAttributes}</th>
                  <th className="num">{colArticles}</th>
                  <th className="hide-tablet">{colDescription}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="is-clickable"
                    onClick={() => navigate(`/tipovi/artikli/${it.id}`)}
                    onKeyDown={(e) => { if (e.key === "Enter") navigate(`/tipovi/artikli/${it.id}`); }}
                    tabIndex={0}
                  >
                    <td className="mono" data-label={colCode}>{it.code}</td>
                    <td data-label={colName}><b>{it.name}</b></td>
                    <td className="num" data-label={colAttributes}>{it.attributeCount > 0 ? it.attributeCount : dash}</td>
                    <td className="num" data-label={colArticles}>{it.articleCount > 0 ? it.articleCount : dash}</td>
                    <td className="hide-tablet" data-label={colDescription}>
                      {it.description
                        ? <span title={it.description.length > DESCRIPTION_MAX ? it.description : undefined}>{truncate(it.description, DESCRIPTION_MAX)}</span>
                        : dash}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

function truncate(text: string, max: number): string {
  const oneLine = text.replace(/\s+/g, " ").trim();
  return oneLine.length > max ? `${oneLine.slice(0, max - 1).trimEnd()}…` : oneLine;
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
